if (!customElements.get("externalvideo-player")) {
  customElements.define(
    "externalvideo-player",
    class ExternalVideoPlayer extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.randomId = Math.random().toString(36).substring(2, 15);
        this.id = `externalvideo-player-${this.randomId}`;
        // add a random id to the element
        this.setAttribute("id", this.id);

        this.videoUrl = this.getAttribute("video-id") || "";
        this.showControls = this.getAttribute("show-controls") === "true";
        this.autoPlay = this.getAttribute("autoplay") === "true";
        this.preloadVideo = this.getAttribute("data-preload-video") === "true";
        if(this.autoPlay) {
          this.setAttribute("data-playing");
        }
        this.hideMuteButton = this.getAttribute("hide-mute-button") === "true";
        this.isMuted = this.getAttribute("muted") === "true";
        this.fitContainer = this.getAttribute("fit-container") === "true";
        this.platform = this.detectPlatform(this.videoUrl);
        this.videoId = this.extractVideoId(this.videoUrl);
        this.player = null;
        this.isPlaying = this.autoPlay;
        this.firstVimeoUnmute = true;
        this.threshold = this.dataset.threshold || 0.5;
        this.threshold = parseFloat(this.threshold);

        this.render();

        this.playerReadyResolve = null;
        this.playerReady = new Promise((resolve) => {
          this.playerReadyResolve = resolve;
        });

        if (this.preloadVideo) {
          this.initPlayer();
        } else {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.initPlayer();
                  this.observer.unobserve(this);
                  this.observer = null;
                }
              });
            },
            { threshold: this.threshold }
          );

          this.observer.observe(this);
        }
      }

      detectPlatform(url) {
        if (/vimeo\.com/.test(url)) return "vimeo";
        if (/youtu\.?be/.test(url)) return "youtube";
        return "unknown";
      }

      extractVideoId(url) {
        if (this.platform === "youtube") {
          const match = url.match(/(?:youtube\.com.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([^"&?/ ]{11})/);
          return match ? match[1] : url;
        } else if (this.platform === "vimeo") {
          const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
          return match ? match[1] : url;
        }
        return url;
      }

      render() {
        this.showOverlay = !this.autoPlay;
        let desktopPosterUrl = this.dataset.desktopPoster || "";
        let mobilePosterUrl = this.dataset.mobilePoster || "";

        if (desktopPosterUrl == "" && mobilePosterUrl == "") {
          this.showOverlay = false;
        }

        if (desktopPosterUrl != "" && mobilePosterUrl == "") {
          mobilePosterUrl = desktopPosterUrl;
        } else if (desktopPosterUrl == "" && mobilePosterUrl != "") {
          desktopPosterUrl = mobilePosterUrl;
        }

        this.innerHTML = `
      <style>
        externalvideo-player#externalvideo-player-${this.randomId} {
          display: block;
          position: relative;
          background: black;
          width: 100%;
          height: 100%;
          background: black;
        }
        externalvideo-player#externalvideo-player-${this.randomId} #player {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
        }
        externalvideo-player#externalvideo-player-${this.randomId} #player iframe {
          position: absolute;
          ${this.fitContainer ? `
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          ` : `
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: calc(100vh * (16 / 9));
          min-height: calc(100vw * (9 / 16));
          `}
        }
        externalvideo-player#externalvideo-player-${this.randomId} .overlay {
          display: ${this.showOverlay ? "none" : "block"};
          position: absolute;
          top: 0;
          left: 0;
          z-index: 2;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        externalvideo-player#externalvideo-player-${this.randomId} .overlay img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        externalvideo-player#externalvideo-player-${this.randomId} .overlay picture {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        externalvideo-player#externalvideo-player-${this.randomId} .controls {
          display: ${this.showControls ? "flex" : "none"};
          gap: 10px;
          position: absolute;
          bottom: 20px;
          right: 20px;
          z-index: 10;
        }
        externalvideo-player#externalvideo-player-${this.randomId} button {
          height: 44px;
          width: 44px;
          cursor: pointer;
          background: none;
          border: 1px solid white;
          border-radius: 50%;
          color: white;
          position: relative;
        }

        externalvideo-player#externalvideo-player-${this.randomId} button#mute-toggle {
          display: ${this.hideMuteButton ? "none" : "block"};
        }

        externalvideo-player#externalvideo-player-${this.randomId} .icon {
          width: 16px;
          height: 16px;
          fill: white;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      </style>
      <div id="player"></div>
      <div class="overlay" id="video-overlay" style="${this.showOverlay ? "" : "display: none;"}">
        <picture>
          <source media="(min-width: 768px)" srcset="${desktopPosterUrl}">
          <source media="(max-width: 767px)" srcset="${mobilePosterUrl}">
          <img src="${desktopPosterUrl}" alt="Video Poster" style="width: 100%; height: 100%; object-fit: cover;">
        </picture>
      </div>
      <div class="controls">
        <button id="play-pause">
          <svg class="icon icon-play" viewBox="0 0 512 512" style="${this.autoPlay ? "display: none;" : ""}">
            <polygon points="96 448 416 256 96 64 96 448"/>
          </svg>
          <svg class="icon icon-pause" viewBox="0 0 512 512" style="${this.autoPlay ? "" : "display: none;"}">
            <path d="M224,432H144V80h80Z"/><path d="M368,432H288V80h80Z"/>
          </svg>
        </button>
        <button id="mute-toggle">
          <svg class="icon icon-mute" viewBox="0 0 16 12" style="${this.isMuted ? "" : "display: none;"}">
            <g clip-path="url(#clip0)">
              <path d="M11.0218 9L12.8885 7.2L14.7551 9L15.9996 7.8L14.1329 6L15.9996 4.2L14.7551 3L12.8885 4.8L11.0218 3L9.77734 4.2L11.644 6L9.77734 7.8L11.0218 9Z" fill="white"/>
              <path d="M3.36508 3.43561H0.634921C0.253968 3.43561 0 3.69344 0 4.08019V7.9477C0 8.33445 0.253968 8.59229 0.634921 8.59229H3.36508L6.98413 11.8989C7.42857 12.1567 8 11.8989 8 11.3832V0.644708C8 0.12904 7.42857 -0.193253 6.98413 0.12904L3.36508 3.43561Z" fill="white"/>
            </g>
            <defs>
              <clipPath id="clip0"><rect width="16" height="12" fill="white"/></clipPath>
            </defs>
          </svg>

          <svg class="icon icon-speaker" viewBox="0 0 16 12" style="${this.isMuted ? "display: none;" : ""}">
            <g clip-path="url(#clip1)">
              <path d="M13.8666 1.11426C13.511 0.771402 12.9777 0.771402 12.6221 1.11426C12.2666 1.45712 12.2666 1.9714 12.6221 2.31426C13.6888 3.34283 14.2221 4.62855 14.2221 5.99997C14.2221 7.3714 13.6888 8.65712 12.6221 9.68569C12.2666 10.0285 12.2666 10.5428 12.6221 10.8857C12.7999 11.0571 13.0666 11.1428 13.2444 11.1428C13.511 11.1428 13.6888 11.0571 13.8666 10.8857C15.1999 9.59997 15.9999 7.88569 15.9999 5.99997C15.9999 4.11426 15.1999 2.39997 13.8666 1.11426Z" fill="white"/>
              <path d="M10.028 2.8131C9.6938 3.13536 9.6938 3.61875 10.028 3.941C10.6815 4.57116 10.9127 5.19089 10.9127 5.99998C10.9127 6.80217 10.5344 7.6433 10.028 8.13162C9.6938 8.45387 9.6938 8.93726 10.028 9.25951C10.157 9.38396 10.5931 9.48423 10.9327 9.39363C11.0329 9.36693 11.1246 9.32365 11.1977 9.25951C12.2426 8.34226 12.5606 6.99409 12.5607 5.99998C12.5607 5.85423 12.5583 5.70869 12.5504 5.56262C12.504 4.71243 12.2671 3.84438 11.1977 2.8131C10.8635 2.49085 10.3622 2.49085 10.028 2.8131Z" fill="white"/>
              <path d="M3.36508 3.43561H0.634921C0.253968 3.43561 0 3.69344 0 4.08019V7.9477C0 8.33445 0.253968 8.59228 0.634921 8.59228H3.36508L6.98413 11.8989C7.42857 12.1567 8 11.8989 8 11.3832V0.644708C8 0.12904 7.42857 -0.193253 6.98413 0.12904L3.36508 3.43561Z" fill="white"/>
            </g>
            <defs>
              <clipPath id="clip1"><rect width="16" height="12" fill="white"/></clipPath>
            </defs>
          </svg>
        </button>
      </div>
    `;
      }

      async initPlayer() {
        if (this.platform === "youtube") {
          await this.loadYouTubeAPI();
        } else if (this.platform === "vimeo") {
          await this.loadVimeoAPI();
          this.createVimeoPlayer();
        }
      }

      async loadYouTubeAPI() {
        if (!window.YT) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          document.head.appendChild(tag);
          await new Promise((resolve) => (tag.onload = resolve));
          window.YT.ready(() => {
            this.createYouTubePlayer();
          });
        } else {
          this.createYouTubePlayer();
        }
      }

      createYouTubePlayer() {
        this.player = new YT.Player(this.querySelector("#player"), {
          videoId: this.videoId,
          playerVars: {
            playsinline: 1,
            autoplay: this.autoPlay ? 1 : 0,
            controls: 0,
            mute: this.isMuted ? 1 : 0,
            showinfo: 0,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady: () => {
              if (this.autoPlay) this.player.playVideo();
              this.playerReadyResolve();
            },
          },
        });

        this.addEventListeners();
      }

      async loadVimeoAPI() {
        if (!window.Vimeo) {
          const tag = document.createElement("script");
          tag.src = "https://player.vimeo.com/api/player.js";
          document.head.appendChild(tag);
          await new Promise((resolve) => (tag.onload = resolve));
        }
      }

      createVimeoPlayer() {
        const iframe = document.createElement("iframe");
        iframe.src = `https://player.vimeo.com/video/${this.videoId}?autoplay=${this.autoPlay ? 1 : 0}&muted=${
          this.isMuted ? 1 : 0
        }&controls=0&playsinline=1&autopause=0`;
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "");
        this.querySelector("#player").appendChild(iframe);
        setTimeout(() => {
          this.player = new Vimeo.Player(iframe);
          this.playerReadyResolve();
          if (this.isMuted) {
            this.player.setVolume(0);
          } else {
            this.player.setVolume(1);
          }
          this.addEventListeners();
          if (this.autoPlay) this.startVimeoAutoplay();
        }, 1000);
      }

      // iOS can ignore the autoplay URL parameter, so start muted playback through the API as well.
      // If it's still blocked (e.g. Low Power Mode) show the play button so one tap starts the video.
      startVimeoAutoplay() {
        const mute = this.isMuted ? this.player.setMuted(true) : Promise.resolve();

        mute
          .then(() => this.player.play())
          .catch(() => {
            this.querySelector(".icon-play").style.display = "block";
            this.querySelector(".icon-pause").style.display = "none";
            this.isPlaying = false;
            this.removeAttribute("data-playing");
          });
      }

      addEventListeners() {
        this.querySelector("#play-pause")?.addEventListener("click", () => {
          if (this.isPlaying) {
            this.pause();
          } else {
            this.play();
          }
        });

        this.querySelector("#mute-toggle")?.addEventListener("click", this.toggleMute.bind(this));

          // Make video player clickable (excludes controls)
        this.querySelector("#player")?.addEventListener("click", (e) => {
          if (!e.target.closest('.controls')) {
            if (this.isPlaying) {
              this.pause();
            } else {
              this.play();
            }
          }
        });

        // Make overlay clickable to start video
        this.querySelector(".overlay")?.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.play();
        });

        // Prevent controls from triggering parent link
        this.querySelector(".controls")?.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }

      async play() {
        if (!this.preloadVideo) {
          await this.playerReady; // ✅ wait for player to be ready
        }

        const playIcon = this.querySelector(".icon-play");
        const pauseIcon = this.querySelector(".icon-pause");

        if (this.querySelector(".overlay")) {
          this.querySelector(".overlay").style.display = "none";
        }

        if (this.platform === "youtube") {
          this.player.playVideo();
        } else if (this.platform === "vimeo") {
          await this.player.play();
        }

        playIcon.style.display = "none";
        pauseIcon.style.display = "block";
        this.isPlaying = true;
        this.setAttribute("data-playing");
      }

      async pause() {
        if (!this.preloadVideo) {
          await this.playerReady;
        }

        const playIcon = this.querySelector(".icon-play");
        const pauseIcon = this.querySelector(".icon-pause");

        if (this.platform === "youtube") {
          this.player.pauseVideo();
        } else if (this.platform === "vimeo") {
          await this.player.pause();
        }

        playIcon.style.display = "block";
        pauseIcon.style.display = "none";
        this.isPlaying = false;
        this.removeAttribute("data-playing");
      }

      toggleMute() {
        const muteIcon = this.querySelector(".icon-mute");
        const speakerIcon = this.querySelector(".icon-speaker");

        if (this.platform === "youtube") {
          if (this.isMuted) {
            this.player.unMute();
            muteIcon.style.display = "none";
            speakerIcon.style.display = "block";
          } else {
            this.player.mute();
            muteIcon.style.display = "block";
            speakerIcon.style.display = "none";
          }
          this.isMuted = !this.isMuted;
        } else if (this.platform === "vimeo") {
          this.player.getVolume().then((volume) => {
            if (volume > 0) {
              this.player.setVolume(0);
              muteIcon.style.display = "block";
              speakerIcon.style.display = "none";
              this.isMuted = true;
              this.firstVimeoUnmute = false;
            } else {
              this.player.setVolume(1);
              muteIcon.style.display = "none";
              speakerIcon.style.display = "block";
              this.isMuted = false;
            }
          });
        }
      }
    }
  );
}